/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Slider,
  Box,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import NoMoreRegister from '../../component/NoMoreRegister';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox, {
  ModbusIUAddressSelectBox,
} from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {
  PrimaryButton,
  AddButton,
  SecondaryButton,
} from '../../component/BasicButton';
//API import
import {
  patchCompositionsMoubusUnit,
  getCompositionsMoubusUnitByOUID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {format, limit, pullDownMenu, sliderMarks} from '../../define';

/**
 * 室内機追加ダイアログ
 * @param props props
 * @returns 室内機追加ダイアログ
 */
const IUAddDialog = (props: {
  /** 室内機追加ボタンの名前 */
  addButtonName: string;
  /** 室外機の情報 */
  ouInfo: apiIF.res.getCompositionsMoubusUnitByOUID;
  /** 室外機の情報（set関数） */
  setOUInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsMoubusUnitByOUID | null>
  >;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, gwID, ouID} = useParams();

  const [iuName, setIUName] = useState(''); //室内機の名前
  //prettier-ignore
  const [iuAddress, setIUAddress] = useState<'1' | '2' | '3' | '4' | '5' | '6' | '7' | '8'>('1'); //アドレス
  const [errorThresholds, setErrorThresholds] = useState('5'); //庫内温度異常とするための閾値
  const [warningThresholds, setWarningThresholds] = useState([-1, 1]); //庫内温度と設定温度の温度差の警戒レベルを判定するための境界
  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示フラグ（追加ダイアログ・登録超過ダイアログ）
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [iuNumber, setIUNumber] = useState(0); //室外機に接続されている室内機の台数
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [iuNameError, setIUNameError] = useState(false); //室内機の名前

  //入力項目のチェック
  useEffect(() => {
    if (iuName === '' || iuNameError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [iuName, iuNameError, apiWait]);

  //登録台数を算出
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || ouID === undefined) {
      return;
    }

    getCompositionsMoubusUnitByOUID(siteID, gwID, ouID).then(response => {
      setIUNumber(Number(response['register-number']));
    });
  }, [props.ouInfo, siteID, gwID, ouID]);

  /**
   * ADD UNITボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenDialog(true);
  };

  /**
   * スライダー変更時処理
   * @param event
   * @param newValue
   * @param activeThumb
   * @returns
   */
  const handleChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }
    const minDistance = 1;

    if (activeThumb === 0) {
      //スライダーの値が許容する値より大きい場合、許容している最大値に変更する
      const adjustedNewMinValue =
        newValue[0] > limit.WARNING_THRESHOLDS_MIN_ALLOWED_MAX_VALUE
          ? limit.WARNING_THRESHOLDS_MIN_ALLOWED_MAX_VALUE
          : newValue[0];

      setWarningThresholds([
        Math.min(adjustedNewMinValue, warningThresholds[1] - minDistance),
        warningThresholds[1],
      ]);
    } else {
      //スライダーの値が許容する値より小さい場合、許容している最小値に変更する
      const adjustedNewMaxValue =
        newValue[1] < limit.WARNING_THRESHOLDS_MAX_ALLOWED_MIN_VALUE
          ? limit.WARNING_THRESHOLDS_MAX_ALLOWED_MIN_VALUE
          : newValue[1];

      setWarningThresholds([
        warningThresholds[0],
        Math.max(adjustedNewMaxValue, warningThresholds[0] + minDistance),
      ]);
    }
  };

  /**
   * OKボタン押下時処理
   */
  const addIU = () => {
    //パスパラメータを取得できない場合
    //室外機情報を取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      ouID === undefined ||
      props.ouInfo === null
    ) {
      return;
    }

    setAPIWait(true);

    //APIに送信するデータを作成
    //追加のため室内機IDは指定しない
    const addIUData = {
      'indoor-unit-name': iuName,
      'address': iuAddress,
      'error-threshold': errorThresholds,
      'temperature-boundary': {
        'low-boundary': String(warningThresholds[0]),
        'high-boundary': String(warningThresholds[1]),
      },
    };
    const editData: apiIF.reqParameters.patchCompositionsMoubusUnit = {
      'indoor-unit-informations': [
        ...props.ouInfo['indoor-unit-informations'],
        addIUData,
      ],
    };
    //情報を編集
    patchCompositionsMoubusUnit(siteID, gwID, ouID, editData)
      .then(() => {
        //室内外機の情報を取得
        return getCompositionsMoubusUnitByOUID(siteID, gwID, ouID);
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['indoor-unit-informations'].sort(
          sortByUnicode('indoor-unit-name')
        );

        props.setOUInfo(response);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setIUName('');
    setErrorThresholds('5');
    setWarningThresholds([-1, 1]);
    setIsOpenDialog(false);
  };

  return (
    <>
      <AddButton
        onClick={handleClickOpen}
        id="IuAdd"
        buttonText={props.addButtonName}
      />
      {/** 登録台数が上限に達している場合は、追加付加のメッセージを表示 */}
      {iuNumber >= limit.REGISTRATION_NUM_MODBUS_IU_MAX ? (
        <NoMoreRegister
          open={isOpenDialog}
          setOpen={setIsOpenDialog}
          title={trans('NoMoreRegist.errorDlg.title')}
          text={trans('NoMoreRegist.errorDlg.msg')}
        />
      ) : (
        <>
          <Dialog
            open={isOpenDialog}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            id="addIuDialog">
            <DialogTitle id="form-dialog-title">
              {trans('IUAddDialog.modbus.dialogBox.title')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {trans('IUAddDialog.modbus.dialogBox.msg')}
              </DialogContentText>
              <Stack spacing={2}>
                {/** 室内機の名前 */}
                <ChkInputTextbox
                  setValue={setIUName}
                  setError={setIUNameError}
                  chkLength={format.BASIC_LENGTH_MAX}
                  type="text"
                  label={trans(
                    'IUAddDialog.modbus.dialogBox.txtField.label.hccvqName'
                  )}
                  value={iuName}
                  id="addIuNameTextForm"
                  class="gwSetting__contents__textBox--unitName"
                  helperText=" "
                  errHelperText={trans(
                    'IUAddDialog.modbus.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  autoFocus={true}
                  margin="dense"
                  chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
                />
                {/** アドレス */}
                <ModbusIUAddressSelectBox
                  setAddress={setIUAddress}
                  label={trans(
                    'IUAddDialog.modbus.dialogBox.txtField.label.address'
                  )}
                  class="gwSetting__contents__textBox--address"
                  id="selectIuUnit"
                />
                {/** 庫内温度異常とするための閾値 */}
                <TextFieldSelectBox
                  label="Error thresholds"
                  value={errorThresholds}
                  setValue={setErrorThresholds}
                  choices={pullDownMenu.ERROR_THRESHOLDS}
                  class="gwSetting__contents__selectBox--errorThresholds"
                />
                {/** 庫内温度と設定温度の温度差の警戒レベルを判定するための境界 */}
                <Stack spacing={1} className="gwSetting__contents__slider">
                  <Box className="gwSetting__contents__slider__title">
                    {trans('IUAddDialog.modbus.dialogBox.slider.title')}
                  </Box>
                  <Box className="gwSetting__contents__slider__input">
                    <Slider
                      getAriaLabel={() =>
                        trans('IUAddDialog.modbus.dialogBox.slider.title')
                      }
                      getAriaValueText={value =>
                        `${value}${trans(
                          'IUAddDialog.modbus.dialogBox.slider.unit'
                        )}`
                      }
                      disableSwap
                      marks={sliderMarks.WARNING_THRESHOLDS}
                      max={limit.WARNING_THRESHOLDS_MAX}
                      min={limit.WARNING_THRESHOLDS_MIN}
                      onChange={handleChange}
                      step={1}
                      value={warningThresholds}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <SecondaryButton
                disabled={apiWait}
                onClick={handleClose}
                buttonText={trans(
                  'IUAddDialog.modbus.dialogBox.buttonTxt.cancel'
                )}
                id={'mv-iuAddCancelBtn'}
              />
              <PrimaryButton
                onClick={addIU}
                buttonText={trans('IUAddDialog.modbus.dialogBox.buttonTxt.ok')}
                id="mv-btnOkAddIu"
                disabled={okBtnDisabled}
                className="BtnOk"
                apiWait={apiWait}
              />
            </DialogActions>
          </Dialog>
          <APIErrorMessage
            open={isOpenErrorDialog}
            setOpen={setIsOpenErrorDialog}
            id="mv-iuAddErrDlgBtn"
          />
        </>
      )}
    </>
  );
};
export default IUAddDialog;
